import { Mail, MessageSquare, Sparkles } from "lucide-react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";

import { StatusBadge } from "../data-display/StatusBadge";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface IntegrationConnection {
  id: string;
  provider: string;
  status: "disconnected" | "connected" | "error" | "pending";
}

const PROVIDERS = [
  {
    key: "twilio",
    name: "Twilio",
    description: "Two-way SMS and calling",
    icon: MessageSquare,
  },
  {
    key: "gmail",
    name: "Gmail",
    description: "Two-way email via Google Workspace",
    icon: Mail,
  },
  {
    key: "outlook",
    name: "Outlook",
    description: "Two-way email via Microsoft 365",
    icon: Mail,
  },
  {
    key: "vapi",
    name: "Vapi",
    description: "AI voice calls, summaries and transcription",
    icon: Sparkles,
  },
] as const;

const STATUS_TONES = {
  connected: "success",
  pending: "warning",
  error: "danger",
  disconnected: "neutral",
} as const;

/**
 * Provider connections for the communications layer. Gravun owns the
 * conversation/message records; providers are replaceable adapters
 * (docs/INTEGRATIONS.md). Marking a provider "pending" reserves the
 * connection, credentials are supplied server-side (secrets store), never
 * stored in this table or entered in the browser.
 */
export const IntegrationsPanel = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();

  const { data: connections } = useGetList<IntegrationConnection>(
    "integration_connections",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "ASC" },
    },
  );

  const connectionFor = (provider: string) =>
    (connections ?? []).find((c) => c.provider === provider);

  const requestConnection = (provider: string) => {
    const existing = connectionFor(provider);
    if (existing) {
      update(
        "integration_connections",
        {
          id: existing.id,
          data: {
            status:
              existing.status === "disconnected" ? "pending" : "disconnected",
          },
          previousData: existing,
        },
        { mutationMode: "pessimistic", onSuccess: () => refresh() },
      );
      return;
    }
    create(
      "integration_connections",
      {
        data: {
          provider,
          status: "pending",
          workspace_id: getActiveWorkspaceId(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify(
            "Connection requested, add the provider credentials to the server secret store to activate it.",
          );
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not update", {
            type: "error",
          }),
      },
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Connect the services that power texting, calling and email. Gravun keeps
        every conversation in your workspace, providers stay replaceable
        adapters, and credentials live only in the server-side secret store.
      </p>
      <div className="flex flex-col gap-2">
        {PROVIDERS.map((provider) => {
          const connection = connectionFor(provider.key);
          const status = connection?.status ?? "disconnected";
          const Icon = provider.icon;
          return (
            <div
              key={provider.key}
              className="flex items-center gap-3 rounded-md border border-border-subtle px-3 py-2.5"
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Icon className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium">{provider.name}</p>
                <p className="text-[12.5px] text-muted-foreground">
                  {provider.description}
                </p>
              </div>
              <StatusBadge
                label={status}
                tone={STATUS_TONES[status] ?? "neutral"}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => requestConnection(provider.key)}
              >
                {status === "disconnected" ? "Connect" : "Disconnect"}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[12px] text-muted-foreground">
        A pending connection activates once its credentials are configured in
        the deployment's secret store (never in the browser or database).
      </p>
    </div>
  );
};
